import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FormTheme, createTheme, deleteTheme, duplicateTheme, fetchThemes, themeStyleOf } from 'services/formThemes';
import { DEFAULT_STYLE, MiniFormSwatch } from './designStudio';
import { IntegrationsTabs } from './pricing';
import ThemeApplyPanel from './themeApplyPanel';

export const THEMES_PATH = '/integrations/themes';

const usageLabel = (theme: FormTheme) => {
    const uses = theme.usedBy || [];
    if (uses.length === 0) return 'Utilisé nulle part';
    return uses
        .map((u) => (u.templateWide ? u.name : `${u.name} · ${u.pages.length} page${u.pages.length > 1 ? 's' : ''}`))
        .join(', ');
};

/**
 * "Mes thèmes" (`/integrations/themes`): the seller's reusable form designs, each with where it is
 * used. A theme is edited on its own page (designEditor.tsx) and applied to whole integrations or
 * single pages from the "Appliquer à…" panel.
 */
const ThemesLibrary = () => {
    const navigate = useNavigate();
    const [themes, setThemes] = useState<FormTheme[] | null>(null);
    const [applying, setApplying] = useState<FormTheme | null>(null);

    const refresh = () => fetchThemes().then(setThemes).catch(() => setThemes([]));
    useEffect(() => { refresh(); }, []);

    const create = async () => {
        const name = window.prompt('Nom du nouveau thème')?.trim();
        if (!name) return;
        try {
            const theme = await createTheme(name.slice(0, 100), DEFAULT_STYLE);
            navigate(`${THEMES_PATH}/${theme._id}`);
        } catch {
            toast.error("Le thème n'a pas pu être créé");
        }
    };

    const duplicate = async (theme: FormTheme) => {
        try {
            await duplicateTheme(theme._id);
            refresh();
        } catch {
            toast.error('Erreur lors de la duplication du thème');
        }
    };

    const remove = async (theme: FormTheme) => {
        const uses = theme.usedBy?.length || 0;
        const warning = uses > 0 ? ` Il est utilisé par ${uses} formulaire${uses > 1 ? 's' : ''}, qui reprendront leur propre design.` : '';
        if (!window.confirm(`Supprimer le thème « ${theme.name} » ?${warning}`)) return;
        try {
            await deleteTheme(theme._id);
            refresh();
        } catch {
            toast.error('Erreur lors de la suppression du thème');
        }
    };

    return (
        <div className="mx-auto flex max-w-[1140px] flex-col gap-5 pb-16 pt-2 font-hanken text-[#1A1613]">
            <ToastContainer position="bottom-right" autoClose={5000} />
            <IntegrationsTabs active="themes" />

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-newsreader text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-[#14110E]">Mes thèmes</h1>
                    <p className="mt-2 max-w-[720px] text-[14.5px] leading-relaxed text-[#6B6357]">
                        Le design de vos formulaires, réutilisable sur <strong className="text-[#3B352C]">plusieurs sites</strong> ou{' '}
                        <strong className="text-[#3B352C]">page par page</strong>. Une modification d'un thème s'applique partout où il est utilisé.
                    </p>
                </div>
                <button type="button" onClick={create} className="rounded-[10px] bg-[#241C5C] px-4 py-2 text-[13.5px] font-semibold text-white hover:opacity-90">
                    + Nouveau thème
                </button>
            </div>

            {themes === null ? (
                <p className="py-10 text-center text-[14px] text-[#8A8172]">Chargement…</p>
            ) : themes.length === 0 ? (
                <div className="rounded-[18px] border border-dashed border-[#DDD5C9] bg-white px-6 py-10 text-center">
                    <p className="text-[14.5px] text-[#3B352C]">Aucun thème pour l'instant.</p>
                    <p className="mt-1 text-[13.5px] text-[#8A8172]">
                        Créez-en un ici, ou depuis l'onglet Apparence d'un formulaire avec « Enregistrer comme thème ».
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {themes.map((theme) => {
                        const look = { ...DEFAULT_STYLE, ...themeStyleOf(theme) };
                        return (
                            <div key={theme._id} className="flex flex-col rounded-[18px] border border-[#EDE7DE] bg-white p-4">
                                <button type="button" onClick={() => navigate(`${THEMES_PATH}/${theme._id}`)} className="text-left">
                                    <div className="mx-auto max-w-[180px]">
                                        <MiniFormSwatch bg={look.backgroundColor} accent={look.buttonBackgroundColor} text={look.textColor} />
                                    </div>
                                    <p className="mt-1 truncate text-[15px] font-semibold text-[#14110E]">{theme.name}</p>
                                    <p className="mt-0.5 line-clamp-2 text-[12.5px] text-[#8A8172]">{usageLabel(theme)}</p>
                                </button>
                                <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1 pt-3 text-[13px] font-semibold">
                                    <button type="button" onClick={() => navigate(`${THEMES_PATH}/${theme._id}`)} className="text-[#241C5C] hover:underline">Modifier</button>
                                    <button type="button" onClick={() => setApplying(theme)} className="text-[#241C5C] hover:underline">Appliquer à…</button>
                                    <button type="button" onClick={() => duplicate(theme)} className="text-[#6B6357] hover:underline">Dupliquer</button>
                                    <button type="button" onClick={() => remove(theme)} className="text-red-600 hover:underline">Supprimer</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {applying && (
                <ThemeApplyPanel theme={applying} onClose={() => setApplying(null)} onApplied={() => refresh()} />
            )}
        </div>
    );
};

export default ThemesLibrary;
